/**
 * Manage the per-workspace ingredient library. Lets you add new entries
 * (which then power autocomplete in the batch table) and edit/delete
 * existing ones.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import * as ingredientsApi from '../api/ingredients';
import type { IngredientLibraryItem } from '../types';
import AppHeader from '../components/AppHeader';
import { PHASES } from '../lib/defaultBatch';

const FUNCTIONS = [
  'humectant',
  'emulsifier',
  'preservative',
  'active',
  'antioxidant',
  'thickener',
  'fragrance',
  'pH adjuster',
  'solvent',
  'other',
];

export default function IngredientsPage() {
  const { user } = useAuth();
  const { show } = useToast();
  const [items, setItems] = useState<IngredientLibraryItem[]>([]);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<IngredientLibraryItem | null>(null);
  const [showForm, setShowForm] = useState(false);

  async function refresh() {
    const { items } = await ingredientsApi.searchIngredients(q, 100);
    setItems(items);
  }

  useEffect(() => {
    const t = setTimeout(refresh, 120);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  async function onSave(body: Partial<IngredientLibraryItem> & { name: string }) {
    if (editing) {
      await ingredientsApi.updateIngredient(editing.id, body);
      show('Updated');
    } else {
      await ingredientsApi.createIngredient(body);
      show('Added');
    }
    setShowForm(false);
    setEditing(null);
    refresh();
  }

  async function onDelete(it: IngredientLibraryItem) {
    if (!confirm(`Delete "${it.name}" from the library?`)) return;
    await ingredientsApi.deleteIngredient(it.id);
    show('Deleted');
    refresh();
  }

  if (!user) return null;

  return (
    <>
      <AppHeader subtitle="Ingredient library" />
      <div className="main">
        <div className="page-eyebrow">Library</div>
        <div className="page-title">Ingredient library</div>
        <div className="page-sub">
          Reusable ingredients with default phases, costs, and typical % ranges.
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input
            className="field-input"
            placeholder="Search by name or INCI"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <button
            className="btn btn-primary"
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
          >
            + Add ingredient
          </button>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">{items.length} ingredients</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                  <th style={th}>Name</th>
                  <th style={th}>INCI</th>
                  <th style={th}>Phase</th>
                  <th style={th}>Function</th>
                  <th style={th}>Typical %</th>
                  <th style={th}>Cost/g</th>
                  <th style={th}>Used</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={td}>{it.name}</td>
                    <td style={td}>{it.inci ?? '—'}</td>
                    <td style={td}>{it.default_phase ?? '—'}</td>
                    <td style={td}>{it.function ?? '—'}</td>
                    <td style={td}>
                      {it.typical_pct_min != null && it.typical_pct_max != null
                        ? `${it.typical_pct_min}–${it.typical_pct_max}%`
                        : '—'}
                    </td>
                    <td style={td}>
                      {it.cost_per_gram != null ? `$${it.cost_per_gram.toFixed(3)}` : '—'}
                    </td>
                    <td style={td}>{it.usage_count}</td>
                    <td style={td}>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ borderColor: 'var(--border)', color: 'var(--ink2)' }}
                        onClick={() => {
                          setEditing(it);
                          setShowForm(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => onDelete(it)}
                        style={{ marginLeft: 4 }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td style={td} colSpan={8}>
                      <span style={{ color: 'var(--ink3)' }}>
                        Nothing yet — add your first ingredient.
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showForm && (
        <IngredientFormModal
          initial={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSave={onSave}
        />
      )}
    </>
  );
}

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  color: 'var(--ink3)',
};
const td: React.CSSProperties = { padding: '8px 12px', color: 'var(--ink2)' };

interface FormProps {
  initial: IngredientLibraryItem | null;
  onClose: () => void;
  onSave: (body: Partial<IngredientLibraryItem> & { name: string }) => Promise<void>;
}

function IngredientFormModal({ initial, onClose, onSave }: FormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [inci, setInci] = useState(initial?.inci ?? '');
  const [supplier, setSupplier] = useState(initial?.default_supplier ?? '');
  const [phase, setPhase] = useState(initial?.default_phase ?? '');
  const [func, setFunc] = useState(initial?.function ?? '');
  const [pctMin, setPctMin] = useState(initial?.typical_pct_min?.toString() ?? '');
  const [pctMax, setPctMax] = useState(initial?.typical_pct_max?.toString() ?? '');
  const [cost, setCost] = useState(initial?.cost_per_gram?.toString() ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await onSave({
        name: name.trim(),
        inci: inci || null,
        default_supplier: supplier || null,
        default_phase: phase || null,
        function: func || null,
        typical_pct_min: pctMin ? Number(pctMin) : null,
        typical_pct_max: pctMax ? Number(pctMax) : null,
        cost_per_gram: cost ? Number(cost) : null,
        notes: notes || null,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{initial ? 'Edit ingredient' : 'Add ingredient'}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
        <form className="modal-body" onSubmit={submit}>
          <div className="field-grid fg-2">
            <div className="field">
              <label className="field-label">Name</label>
              <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="field">
              <label className="field-label">INCI</label>
              <input className="field-input" value={inci} onChange={(e) => setInci(e.target.value)} />
            </div>
            <div className="field">
              <label className="field-label">Default supplier</label>
              <input className="field-input" value={supplier} onChange={(e) => setSupplier(e.target.value)} />
            </div>
            <div className="field">
              <label className="field-label">Default phase</label>
              <select className="field-select" value={phase} onChange={(e) => setPhase(e.target.value)}>
                <option value="">—</option>
                {PHASES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="field-label">Function</label>
              <select className="field-select" value={func} onChange={(e) => setFunc(e.target.value)}>
                <option value="">—</option>
                {FUNCTIONS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="field-label">Cost per gram (USD)</label>
              <input className="field-input" type="number" step="0.001" value={cost} onChange={(e) => setCost(e.target.value)} />
            </div>
            <div className="field">
              <label className="field-label">Typical % min</label>
              <input className="field-input" type="number" step="0.01" value={pctMin} onChange={(e) => setPctMin(e.target.value)} />
            </div>
            <div className="field">
              <label className="field-label">Typical % max</label>
              <input className="field-input" type="number" step="0.01" value={pctMax} onChange={(e) => setPctMax(e.target.value)} />
            </div>
            <div className="field span2">
              <label className="field-label">Notes</label>
              <textarea className="field-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
        </form>
        <div className="modal-footer">
          <button className="btn btn-white" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={submit} disabled={busy || !name.trim()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
